/**
 * AvisoDoPush — o aviso flutuante de quando o push deste aparelho não volta sozinho (plano "Push: religar sozinho ou avisar", Decisão 8).
 *
 * Quem o monta é o `ReinscricaoDoPush`, quando a sincronização do push devolve `{tipo: "aviso", qual}`:
 *   - `permissao-voltou-a-perguntar` e `precisa-de-clique` → "religar": botão "Ligar de novo" (um clique, que é o gesto que o navegador
 *     exige para pedir a permissão ou inscrever) e "Agora não", que cala por 7 dias;
 *   - `bloqueado` → "desbloquear": os passos para liberar no navegador (`PassosParaDesbloquear`) e "Entendi", que cala por 30 dias (a
 *     pessoa só age no navegador; sem botão de ligar).
 * O X de cada um faz o mesmo que o botão de calar.
 *
 * Sem aviso repetitivo: só aparece se o silêncio do tipo não estiver ativo E se o aviso `qual` ainda não foi mostrado nesta sessão da aba
 * (`sessionStorage`; remontar na mesma sessão não o mostra de novo — Review Focus 2). Some sozinho quando a causa some, e quem decide é
 * o `usePushSubscription` (a inscrição voltou, nos de religar; a permissão deixou de ser `denied`, no de desbloquear). Quem zera os
 * silêncios não é este componente: é a sincronização (`sincronizarPush`), quando conclui `ok` ou `religado` — aí o `limparSilencios`
 * apaga os dois silêncios (`localStorage`) E o "já mostrado" dos três avisos (`sessionStorage`), e o próximo problema avisa na hora.
 * Enquanto nenhuma sincronização conclui `ok`/`religado`, o silêncio e o "já mostrado" valem como estavam.
 *
 * Enquanto há uma atualização do app pronta para ativar (`useServiceWorkerUpdate().updateReady`, o mesmo hook do `UpdatePrompt`), o aviso
 * fica fora e NÃO grava o "já mostrado": no celular o `UpdatePrompt` (`bottom-[9rem]`, desenhado depois) o cobriria, sobrando só os
 * botões. Ao "Atualizar" a página recarrega e o aviso aparece normalmente. Se o aviso já estava na tela quando a atualização ficou
 * pronta (o SW novo é detectado alguns segundos depois da abertura), ele sai e o "já mostrado" que acabou de gravar é desfeito: sem isso
 * ele não voltaria depois do "Atualizar" até a sessão acabar. Só vale para o aviso que a pessoa não dispensou (esses gravam o silêncio) e
 * cuja causa não sumiu. (Chamar o hook aqui de novo é seguro: ele só assina o pub/sub de `sw-registration`; quem registra o Workbox é
 * `registerServiceWorker`, uma vez só.)
 *
 * Molde: o cartão flutuante do convite `NotificationPrompt` (mesmo contêiner, mesmos componentes de UI). Os textos vêm do app que o monta
 * (`TextosDoPush.aviso`): o portal nunca vê a marca interna.
 */
import { useEffect, useRef, useState } from "react";
import { BellOff, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { usePushSubscription } from "@/hooks/usePushSubscription";
import { useServiceWorkerUpdate } from "@/hooks/useServiceWorkerUpdate";
import {
  avisoJaMostrado,
  avisoSilenciado,
  desfazerAvisoMostrado,
  registrarAvisoMostrado,
  silenciarAviso,
  type TipoDeSilencio,
} from "@/lib/push/memoriaDoPush";
import type { QualAviso } from "@/lib/push/sincronizarPush";

import type { TextosDoPush } from "./CartaoPushDoAparelho.textos";
import { PassosParaDesbloquear } from "./PassosParaDesbloquear";

/**
 * O 3º passo do aviso de desbloqueio: um trecho do passo do cartão ("Recarregue a página e ligue os avisos nesta tela."), sem o "ligue
 * os avisos nesta tela". Depois de desbloquear e recarregar, a sincronização religa sozinha, e o aviso nem tem botão de ligar.
 */
const TERCEIRO_PASSO_DO_AVISO = "Recarregue a página.";

/** Por quanto tempo cada tipo de aviso fica calado depois de "Agora não"/"Entendi"/X (Decisão 8). */
const DIAS_DE_SILENCIO: Record<TipoDeSilencio, number> = { religar: 7, desbloquear: 30 };

interface Props {
  qual: QualAviso;
  /** Qual API de push falar (a do botão "Ligar de novo"): `PUSH_BASE_INTERNO` ou `PUSH_BASE_PORTAL`. */
  basePath: string;
  /** Os textos do app que monta o aviso. */
  textos: TextosDoPush;
}

export function AvisoDoPush({ qual, basePath, textos }: Props): JSX.Element | null {
  const ehDesbloqueio = qual === "bloqueado";
  const tipo: TipoDeSilencio = ehDesbloqueio ? "desbloquear" : "religar";
  const { permission, subscription, isSubscribing, error, subscribe } =
    usePushSubscription(basePath);
  const { updateReady } = useServiceWorkerUpdate();

  // Decidido uma vez, na montagem: o silêncio e o "já mostrado" não mudam por si só no meio da vida do aviso. Quem troca de `qual`
  // remonta o componente (o `ReinscricaoDoPush` passa `key={qual}`).
  const [visivel, setVisivel] = useState<boolean>(
    () => !avisoSilenciado(tipo, Date.now()) && !avisoJaMostrado(qual)
  );

  const causaResolvida = ehDesbloqueio ? permission !== "denied" : subscription !== null;
  const naTela = visivel && !causaResolvida && !updateReady;

  // Resolvido, resolvido: a causa que some trava o aviso fora da tela pelo resto desta montagem. Sem isto, a causa que VOLTA (a pessoa
  // ligou, depois desligou em Preferências de propósito e o hook do aviso releu `subscription === null`; ou a permissão foi de `denied`
  // para `granted` e voltou) faria o aviso reaparecer, e ele aparece uma vez por sessão (Decisão 8, Review Focus 2). A próxima vez que o
  // problema acontecer vem como um resultado novo da sincronização, e o `ReinscricaoDoPush` monta um aviso novo; esse avisa na hora se,
  // pelo meio, a sincronização concluiu `ok`/`religado` (o `limparSilencios` zera os silêncios e o "já mostrado"). Sem isso, valem o
  // silêncio e o "já mostrado" de antes.
  useEffect(() => {
    if (causaResolvida) setVisivel(false);
  }, [causaResolvida]);

  // Grava que este aviso já apareceu nesta sessão, só quando ele realmente está na tela (não gasta a vez do aviso calado, do resolvido
  // nem do que está esperando a atualização do app).
  const jaEsteveNaTela = useRef(false);
  useEffect(() => {
    if (!naTela) return;
    jaEsteveNaTela.current = true;
    registrarAvisoMostrado(qual);
  }, [naTela, qual]);

  // O aviso que estava na tela e saiu só porque a atualização do app ficou pronta não gasta o "já mostrado": desfaz o que acabou de
  // gravar, para ele voltar depois do "Atualizar". Fica de fora quem a pessoa dispensou (silêncio gravado e `visivel=false`) e quem teve
  // a causa resolvida: esses já terminaram, e o "já mostrado" deles vale. O `visivel` não muda aqui: o aviso continua só esperando a
  // atualização.
  useEffect(() => {
    if (updateReady && jaEsteveNaTela.current && visivel && !causaResolvida) {
      desfazerAvisoMostrado(qual);
    }
  }, [updateReady, visivel, causaResolvida, qual]);

  if (!naTela) return null;

  const dispensar = () => {
    silenciarAviso(tipo, Date.now(), DIAS_DE_SILENCIO[tipo]);
    setVisivel(false);
  };

  const { aviso } = textos;

  return (
    <div
      role="status"
      data-testid="aviso-do-push"
      className="fixed inset-x-4 bottom-[5.5rem] z-50 rounded-lg border bg-background/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-96"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <BellOff aria-hidden="true" className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            {ehDesbloqueio ? aviso.desbloquearTitulo : aviso.religarTitulo}
          </p>

          {ehDesbloqueio ? (
            <div className="space-y-2 text-xs text-muted-foreground">
              <p>{aviso.desbloquearIntro}</p>
              {/* A nota do iPhone (rodapé) já vem dentro do `PassosParaDesbloquear`: não repetir aqui. */}
              <PassosParaDesbloquear textos={textos} terceiroPasso={TERCEIRO_PASSO_DO_AVISO} />
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {qual === "permissao-voltou-a-perguntar"
                ? aviso.religarCorpoPermissao
                : aviso.religarCorpoSemInscricao}
            </p>
          )}

          {!ehDesbloqueio && error && (
            <p
              role="alert"
              data-testid="aviso-do-push-erro"
              className="mt-1 break-words text-xs text-destructive"
            >
              {error}
            </p>
          )}

          <div className="mt-3 flex items-center gap-2">
            {ehDesbloqueio ? (
              <Button size="sm" onClick={dispensar} data-testid="aviso-do-push-entendi">
                {aviso.entendi}
              </Button>
            ) : (
              <>
                <Button
                  size="sm"
                  onClick={() => void subscribe()}
                  disabled={isSubscribing}
                  data-testid="aviso-do-push-ligar"
                >
                  {isSubscribing ? "Ligando…" : error ? "Tentar de novo" : aviso.religarBotao}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={dispensar}
                  data-testid="aviso-do-push-agora-nao"
                >
                  {aviso.agoraNao}
                </Button>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={dispensar}
          aria-label="Dispensar"
          data-testid="aviso-do-push-dispensar"
          className="-mr-1 -mt-1 shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
